import { useEffect, useState } from 'react';
import { Accordion, SubAccordion } from '../Accordion';
import { KebabMenu } from '../KebabMenu';
import badgesIcon from '../../assets/icons/badges-icon.svg';
import coinsIcon from '../../assets/icons/coins-icon.svg';
import challengesIcon from '../../assets/icons/challenges-icon.svg';
import ratingsIcon from '../../assets/icons/rating-icon.svg';
import badgesSubIcon from '../../assets/icons/badges-sub-icon.svg';
import coinsSubIcon from '../../assets/icons/coins-sub-icon.svg';
import challengesSubIcon from '../../assets/icons/challenges-sub-icon.svg';
import gamePreviewIcon from '../../assets/icons/preview-icon.svg';
import defaultGameIcon1 from '../../assets/icons/default-game-icon-1.svg';
import optionsIcon from '../../assets/icons/options-icon.svg';
import './index.scss';
import { NoDataAvailable } from '../NoDataAvailable';
import { useAppSelector } from '../../hooks/redux';
import { IGameDraftUpdateResponse, IGameElementInfo, IGameInfo, IndexedGameInfo } from '../../interfaces/game-info';
import { IPreviewPanelProps } from '../../interfaces/panel';
import { ConfirmationPopupWithReason, ElementPopup, NewGamePopup, NotificationPopup } from '../Popup';
import { AnimatePresence } from 'framer-motion';
import { IUserInfoState } from '../../interfaces/slices';
import { IAPIResponse } from '../../interfaces/api';
import { updateDraft } from '../../api/draft';
import { IElementsInfo, IndexedElementsInfo } from '../../interfaces/elements';
import { checkAPIResponse } from '../../utils/utils';

export const PreviewPanel = (props: IPreviewPanelProps) => {
    const userInfo: IUserInfoState = useAppSelector((state) => state.userInfo);
    const gameInfo: IndexedGameInfo = useAppSelector((state) => state.publishedGames.info);
    const elementsInfo: IndexedElementsInfo = useAppSelector((state) => state.gameProperties.elements);

    const [selectedGameInfo, setSelectedGameInfo] = useState<IGameInfo | null>(null);
    const [viewElementPopupVisibility, setViewElementPopupVisibility] = useState<{ isVisible: boolean, elementInfo: IGameElementInfo | null }>({ isVisible: false, elementInfo: null });
    const [editGamePopupVisibility, setEditGamePopupVisibility] = useState<{ isVisible: boolean, gameInfo: IGameInfo | null }>({ isVisible: false, gameInfo: null });
    const [deleteGamePopup, setDeleteGamePopup] = useState<{ visibility: boolean, title: string, message: string, onConfirmation: Function, onCancellation: Function }>({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } });
    const [notificationPopup, setNotificationPopup] = useState<{ visibility: boolean, message: string }>({ visibility: false, message: '' });

    const viewElementDetails = (selectedElementId: string, selectedElementName: string): void => {
        if (props.selectedGameName != null && props.selectedGameName !== "" && selectedGameInfo != null) {
            for (let i = 0; i < selectedGameInfo.elements.length; i++) {
                if (selectedGameInfo.elements[i].id === selectedElementId && selectedGameInfo.elements[i].name === selectedElementName) {
                    setViewElementPopupVisibility({ isVisible: true, elementInfo: selectedGameInfo.elements[i] });
                    break;
                }
            }
        }
    }

    const hideElementPopup = (): void => {
        setViewElementPopupVisibility({ isVisible: false, elementInfo: null });
    }

    const onKebabMenuOptionSelected = (option: 'edit' | 'delete', gameName: string): void => {
        const selectedGameInfo: IGameInfo = gameInfo[gameName];

        if (selectedGameInfo != null) {
            switch (option) {
                case 'edit':
                    if (gameName != null && gameName !== "") {
                        setEditGamePopupVisibility({ isVisible: true, gameInfo: selectedGameInfo });
                    }
                    break;
                case 'delete':
                    setDeleteGamePopup({
                        visibility: true,
                        title: 'Delete Game',
                        message: `Are you sure you want to delete game ${gameName}?`,
                        onConfirmation: (reason: string) => saveDeletedGameAsDraft(selectedGameInfo, reason),
                        onCancellation: hideDeleteGamePopup
                    });
                    break;
            }
        }
    }

    const hideDeleteGamePopup = (): void => {
        setDeleteGamePopup({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } })
    }

    const saveDeletedGameAsDraft = async (gameInfo: IGameInfo, reason: string) => {
        hideDeleteGamePopup();
        let selectedGameInfo: IGameInfo = { ...gameInfo };

        selectedGameInfo.operation = 'delete';
        selectedGameInfo.modifyReason = reason;

        const apiResponse: IAPIResponse<IGameDraftUpdateResponse> = await updateDraft(userInfo.lanid, selectedGameInfo);
        const formattedResponse = checkAPIResponse<IGameDraftUpdateResponse>(apiResponse);

        if (formattedResponse.status === false) {
            setNotificationPopup({ visibility: true, message: `Unable to save game ${selectedGameInfo.gameName} as a draft (${formattedResponse.message})` });
        }
    }

    const getExistingElementsInfo = (): Array<IGameElementInfo> => {
        if (props.selectedGameName != null && props.selectedGameName !== '') {
            const selectedGameInfo: IGameInfo = gameInfo[props.selectedGameName];
            return selectedGameInfo.elements;
        }
        else {
            return [];
        }
    }

    useEffect(() => {
        if (props.selectedGameName != null && props.selectedGameName !== "") {
            let info: IGameInfo | null = gameInfo[props.selectedGameName];
            if (info != null) {
                setSelectedGameInfo(info);
            }
        }
    }, [props.selectedGameName]);

    return <div className='preview-list-wrapper'>
        <div className='preview-list-header-container'>
            <img src={gamePreviewIcon} alt='' />
            <span>Preview</span>
        </div>
        <div className='preview-list-body-container'>
            {props.selectedGameName == null || props.selectedGameName === "" || selectedGameInfo == null ?
                <div className='preview-list-body-wrapper nothing-selected'>
                    <div className='preview-list-body-nothing-selected-container'>
                        <NoDataAvailable message='Select a game to preview' />
                    </div>
                </div>
                :
                <div className='preview-list-body-wrapper'>
                    <div className='game-info-container'>
                        <div className='game-info-image-container'>
                            <img src={defaultGameIcon1} alt='' />
                        </div>
                        <div className='game-info-details-container'>
                            <span className='game-name-text'>{selectedGameInfo.gameName}</span>
                            <span className='game-status-text'>{`Type: ${selectedGameInfo.status}`}</span>
                            <span className='game-description-text'>{selectedGameInfo.description}</span>
                        </div>
                        <div className='game-info-miscellaneous-container'>
                            {userInfo.roles.maker === true &&
                                <KebabMenu
                                    optionsIcon={optionsIcon}
                                    gameName={selectedGameInfo.gameName}
                                    onOptionClicked={(option: 'edit' | 'delete', gameName: string) => onKebabMenuOptionSelected(option, gameName)}
                                />
                            }
                        </div>
                    </div>
                    <div className='game-details-container'>
                        <>
                            {Object.values(elementsInfo).map((item: IElementsInfo, index: number) => (
                                <div className='accordion-container' key={item.id}>
                                    <Accordion
                                        // icon={badgesIcon} 
                                        // icon={coinsIcon}
                                        // icon={ratingsIcon}
                                        // icon={challengesIcon}
                                        // icon={``}
                                        title={item.name}
                                        count={null}
                                        content={
                                            <>
                                                {selectedGameInfo.elements.length > 0 &&
                                                    <>
                                                        {selectedGameInfo.elements.map((elementInfo: IGameElementInfo, index: number) => {
                                                            if (elementInfo.type === item.id) {
                                                                return <SubAccordion
                                                                    key={elementInfo.name}
                                                                    title={elementInfo.name}
                                                                    content={''}
                                                                    onViewButtonClick={() => viewElementDetails(elementInfo.id, elementInfo.name)}
                                                                // icon={challengesSubIcon}
                                                                // icon={coinsSubIcon}
                                                                // icon={badgesSubIcon}
                                                                />
                                                            }
                                                        })}
                                                    </>
                                                }
                                            </>
                                        }
                                    />
                                </div>
                            )
                            )}
                        </>
                    </div>
                </div>
            }
        </div>
        <AnimatePresence>
            {viewElementPopupVisibility.isVisible === true &&
                <ElementPopup
                    operation={'view'}
                    updateElementInfo={viewElementPopupVisibility.elementInfo}
                    onCancelEvent={hideElementPopup}
                    onConfirmEvent={() => { }}
                    onCloseEvent={hideElementPopup}
                    existingElementInfo={getExistingElementsInfo()}
                />
            }
            {editGamePopupVisibility.isVisible === true &&
                <NewGamePopup
                    isApprovalMode={false}
                    updationGameInfo={editGamePopupVisibility.gameInfo}
                    closePopup={() => setEditGamePopupVisibility({ isVisible: false, gameInfo: null })}
                />
            }
            {deleteGamePopup.visibility === true &&
                <ConfirmationPopupWithReason
                    title={deleteGamePopup.title}
                    message={deleteGamePopup.message}
                    isModifyReasonRequired={true}
                    onConfirmation={deleteGamePopup.onConfirmation}
                    onCancellation={deleteGamePopup.onCancellation}
                />
            }
            {notificationPopup.visibility === true &&
                <NotificationPopup
                    message={notificationPopup.message}
                    closePopup={() => setNotificationPopup({ visibility: false, message: '' })}
                />
            }
        </AnimatePresence>
    </div>
}